import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { NullValidationHandler, OAuthService } from "angular-oauth2-oidc";

import { config } from "./../app/authConfig";
import { SECURE_LOCAL_STORAGE } from "../classes/app.constants";
import { ISecureStorage } from "../classes/interfaces/isecurestorage";
import { Token } from "../models";
import { BehaviorSubject, Subject } from "rxjs";
import { ClaimsEnum } from "../models/enums";
import { filter } from "rxjs/operators";

import { environment } from '../environments/environment';

@Injectable()
export class AuthorizationService {
    private _decodedAccessToken: any;
    private _decodedIDToken: any;
    get decodedAccessToken() { return this._decodedAccessToken; }
    get decodedIDToken() { return this._decodedIDToken; }
    //  claims persistence storage setup
    private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    isAuthenticated$ = this.isAuthenticatedSubject.asObservable;

    isFullAccessUser: boolean = false;
    isLimitedAccessUser: boolean = false;
    isReadOnlyUser: boolean = false;

    private authMessageSource = new Subject<string>();
    nameChangeMessage = this.authMessageSource.asObservable();

    private blcClaimsKey: string = "blcClaimsKey";
    cachedClaimsRecord: {
        storedClaims: Record<string, any> | null;
        storingClaims?: Record<string, any>;
    } = {
            storedClaims: null,
        };

    set storingClaims(claims: Record<string, any>) {
        this.cachedClaimsRecord.storedClaims = claims;
    }

    get storingClaims(): Record<string, any> | null {
        return this.cachedClaimsRecord.storedClaims;
    }

    private cachedRoles: string[] = null;
    private cachedUserName: string = null;
    private storageKey: string = "blcAuthToken";
    private token: Token = null;

    constructor(
        private oAuthService: OAuthService,
        private router: Router,
        @Inject(SECURE_LOCAL_STORAGE) private storage: ISecureStorage
    ) {
        this.oAuthService.configure(config);
        this.setupSessionPersistence();
        this.oAuthService.timeoutFactor = 0.3;
        this.oAuthService.setupAutomaticSilentRefresh();
        this.oAuthService.loadDiscoveryDocumentAndTryLogin().then(() => {
            if (!this.authenticated()) {
                this.login();
            } else {
                this.isAuthenticatedSubject.next(true);
                this.restoreIdentityClaims();
            }
        });
        this.oAuthService.events.pipe(filter(e => e.type === 'token_received')).subscribe(() => {
            this.isAuthenticatedSubject.next(true);
            this.restoreIdentityClaims();
        });
        this.oAuthService.events.pipe(filter(e => e.type === 'token_validation_error')).subscribe(() => {
            this.isAuthenticatedSubject.next(false);
        });
    }

    private handleNewToken(): void {
        this._decodedAccessToken = this.oAuthService.getAccessToken();
        this._decodedIDToken = this.oAuthService.getIdToken();
        const claims = this.oAuthService.getIdentityClaims();
        if (claims) {
            this.storage.setItem(this.blcClaimsKey, claims);
            this.cachedClaimsRecord.storedClaims = claims;
        }
    }

    async initAuth(): Promise<any> {
        return new Promise<void>(() => {
            // setup oauthService
            this.oAuthService.configure(config);
            this.oAuthService.setStorage(localStorage);
            this.oAuthService.tokenValidationHandler = new NullValidationHandler();
            this.login();
            this.handleNewToken();
        });
    }

    restoreIdentityClaims(): void {
        //  getting the claims from the storage
        this.cachedClaimsRecord.storedClaims = this.storage.getItem<Record<string, any>>(this.blcClaimsKey);
        //  if the claims are empty, then get a new token and store the claims
        if (!this.cachedClaimsRecord.storedClaims || JSON.stringify(this.cachedClaimsRecord.storedClaims) === '{}') {
            this.handleNewToken();
        }
        //  setting the claims to the oauthService
        this.oAuthService.getIdentityClaims = () => this.cachedClaimsRecord.storedClaims;
        //  setting the subscribed message to whatever component is listening like the toolbar component
        this.authMessageSource.next(`(${this.getEnvironmentType()} - ${this.getAccessLevelTitle()}) - ${this.getPulteUsername()}`);
    }

    private setupSessionPersistence(): void {
        this.oAuthService.setStorage(localStorage);
        const token = this.oAuthService.getAccessToken();
        if (token && this.oAuthService.hasValidAccessToken()) {
            this.isAuthenticatedSubject.next(true);
        } else {
            this.isAuthenticatedSubject.next(false);
        }
    }

    authenticated(): boolean {
        return !!this.oAuthService.hasValidIdToken();
    }

    checkBlcUserAccess(isFullAccessRequired: boolean = false): void {
        if ((isFullAccessRequired && this.isBlcExceptionsUser()) || (!this.isBlcFullAccessUser() && !this.isBlcExceptionsUser() && !this.isBlcReadOnlyUser())) {
            alert('You are not authorized to use this page due to your access level.');
            this.router.navigate(['unauthorized']);
        }
    }

    dropCache(): void {
        this.cachedRoles = null;
        this.cachedUserName = null;
    }

    getAccessLevelTitle(): string {
        if (this.isBlcExceptionsUser()) {
            return 'Restricted Access';
        } else if (this.isBlcFullAccessUser()) {
            return 'Full Access';
        } else if (this.isBlcReadOnlyUser()) {
            return 'Read Only Access';
        } else {
            return 'Unauthorized';
        }
    }

    getEnvironmentType(): string {
        return environment.type !== 'PROD' ? environment.type : 'PROD';
    }

    getAccessToken(): string {
        return this.oAuthService.getAccessToken();
    }

    async getClaims(): Promise<Record<string, any>> {
        var returnedClaims = this.oAuthService.getIdentityClaims();
        this.cachedClaimsRecord.storedClaims = returnedClaims;
        return returnedClaims;
    }

    getPulteUsername(): string {
        return `PHCORP\\${this.getUsername().replace('@pulte.com', '').replace('@pultegroup.com', '')}`;
    }

    getUsername(): string {
        if (this.cachedUserName === null || this.cachedUserName === "") {
            this.cachedUserName = this.cachedClaimsRecord.storedClaims?.["preferred_username"] ?? "";
        }

        return this.cachedUserName;
    }

    isBlcExceptionsUser(): boolean {
        let retval = false;
        this.isLimitedAccessUser = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.cachedClaimsRecord.storedClaims?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.blcexceptions)) {
                retval = true;
                this.isLimitedAccessUser = true;
            }
        });

        return retval;
    }

    isBlcFullAccessUser(): boolean {
        let retval = false;
        this.isFullAccessUser = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.cachedClaimsRecord.storedClaims?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.blcfullaccess)) {
                retval = true;
                this.isFullAccessUser = true;
            }
        });

        return retval;
    }

    isBlcReadOnlyUser(): boolean {
        let retval = false;
        this.isReadOnlyUser = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.cachedClaimsRecord.storedClaims?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.blcreadonly)) {
                retval = true;
                this.isReadOnlyUser = true;
            }
        });

        return retval;
    }

    isInRole(): boolean {
        let retval = false;

        if (!this.cachedRoles || this.cachedRoles.length == 0) {
            this.cachedRoles = this.cachedClaimsRecord.storedClaims?.['roles'] ?? [];
        }

        this.cachedRoles.forEach(element => {
            if (element?.includes(ClaimsEnum.blcfullaccess) || element?.includes(ClaimsEnum.blcexceptions) || element?.includes(ClaimsEnum.blcreadonly)) {
                retval = true;
            }
        });

        return retval;
    }

    login(): void {
        this.oAuthService.initLoginFlow();
    }

    logout(): void {
        this.oAuthService.logOut();
    }
}
